'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function ArchitecturePage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-4xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Architecture Overview
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus is built on a modern, scalable architecture designed for performance, 
                            reliability, and developer experience. Here's how everything fits together.
                        </p>

                        {/* Frontend Stack */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🎨 Frontend Stack
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-2">Next.js 14</h3>
                                    <p className="text-gray-300 text-sm">
                                        React framework with App Router, server-side rendering, and automatic optimization. 
                                        Configured for standalone deployment with enhanced webpack settings for media handling.
                                    </p>
                                    <div className="mt-2 font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">
                                        output: 'standalone', productionBrowserSourceMaps: true
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-2">Tailwind CSS</h3>
                                    <p className="text-gray-300 text-sm">
                                        Utility-first CSS framework with custom scrollbar support and extensive color palette. 
                                        Integrated with CSS modules for component-specific styling.
                                    </p>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-2">Framer Motion</h3>
                                    <p className="text-gray-300 text-sm">
                                        Advanced animation library powering smooth page transitions, block animations, and 
                                        interactive elements throughout the platform.
                                    </p>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-2">React DnD</h3>
                                    <p className="text-gray-300 text-sm">
                                        Drag and drop functionality for block reordering with touch support for mobile devices. 
                                        Automatic backend detection (HTML5 vs Touch).
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Backend Stack */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🗄️ Backend Stack
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-emerald-400 mb-2">Supabase</h3>
                                    <p className="text-gray-300 text-sm">
                                        Open-source Firebase alternative providing authentication, PostgreSQL database, 
                                        real-time subscriptions, and row-level security (RLS) policies.
                                    </p>
                                    <div className="mt-2 space-y-1">
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">
                                            Authentication: Email, OAuth providers (Google, GitHub)
                                        </div>
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">
                                            Database: PostgreSQL with real-time capabilities
                                        </div>
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">
                                            Storage: File uploads, image optimization
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-2">PostgreSQL Schema</h3>
                                    <p className="text-gray-300 text-sm">
                                        Relational database with sophisticated role-based access control, resource limits, 
                                        and optimized queries for content delivery.
                                    </p>
                                    <div className="mt-2 grid grid-cols-2 gap-2">
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">profiles</div>
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">pages</div>
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">content_blocks</div>
                                        <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">user_roles</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Infrastructure */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                ☁️ Infrastructure & Deployment
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-2">AWS Amplify</h3>
                                    <p className="text-gray-300 text-sm">
                                        Hosting platform with automatic deployments, CDN distribution, and serverless backend integration. 
                                        Configured for Next.js with custom build settings.
                                    </p>
                                    <div className="mt-2 font-mono text-xs text-gray-400 bg-neutral-800 p-2 rounded">
                                        amplify.yml: npm ci → npm run build → deploy
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-400 mb-2">GitHub Actions</h3>
                                    <p className="text-gray-300 text-sm">
                                        Continuous integration and deployment pipeline triggering automatic builds and deployments 
                                        to AWS Amplify on push to main branch.
                                    </p>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-2">CDN & Caching</h3>
                                    <p className="text-gray-300 text-sm">
                                        Global content delivery with intelligent caching for static assets, images, and frequently 
                                        accessed content. Image optimization with Next.js Image component.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Data Flow */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🔄 Data Flow Architecture
                            </h2>
                            <div className="space-y-4">
                                <div className="font-mono text-sm text-gray-300 bg-neutral-800 p-4 rounded-lg">
                                    <div className="text-center mb-4 text-white font-bold">Request Flow</div>
                                    <div className="space-y-2">
                                        <div>1. User visits → <span className="text-blue-400">autofoc.us/username/page</span></div>
                                        <div>2. Next.js SSR → <span className="text-green-400">Fetch from Supabase</span></div>
                                        <div>3. Profile + Page + Blocks → <span className="text-purple-400">Render Components</span></div>
                                        <div>4. Real-time subscription → <span className="text-orange-400">Live Updates</span></div>
                                        <div>5. User interactions → <span className="text-cyan-400">API mutations</span></div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-2">Real-time Features</h3>
                                    <p className="text-gray-300 text-sm">
                                        Supabase real-time subscriptions enable live collaborative editing, instant block updates, 
                                        and synchronized content changes across all connected clients.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Performance Optimizations */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                ⚡ Performance & Scalability
                            </h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-2">Frontend Optimizations</h3>
                                    <ul className="text-gray-300 text-sm space-y-1">
                                        <li>• Code splitting with Next.js dynamic imports</li>
                                        <li>• Image optimization with WebP/AVIF support</li>
                                        <li>• Bundle analysis and tree shaking</li>
                                        <li>• Lazy loading for non-critical components</li>
                                        <li>• Service worker for offline capabilities</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-2">Backend Optimizations</h3>
                                    <ul className="text-gray-300 text-sm space-y-1">
                                        <li>• PostgreSQL indexes on frequently queried columns</li>
                                        <li>• Row-level security for efficient data filtering</li>
                                        <li>• Connection pooling with Supabase</li>
                                        <li>• Optimized queries with selective field fetching</li>
                                        <li>• Caching layers for static content</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Security */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🔐 Security Architecture
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-2">Authentication & Authorization</h3>
                                    <p className="text-gray-300 text-sm mb-2">
                                        Multi-layered security with JWT tokens, role-based access control, and granular permissions.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <div className="font-semibold text-gray-200 mb-1">Authentication</div>
                                            <ul className="text-gray-400 space-y-1">
                                                <li>• Supabase Auth with JWT tokens</li>
                                                <li>• OAuth providers (Google, GitHub)</li>
                                                <li>• Email verification required</li>
                                                <li>• Session management with refresh tokens</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <div className="font-semibold text-gray-200 mb-1">Authorization</div>
                                            <ul className="text-gray-400 space-y-1">
                                                <li>• Role-based permissions system</li>
                                                <li>• Resource limits by subscription tier</li>
                                                <li>• Row-level security policies</li>
                                                <li>• Admin panel access controls</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Development Workflow */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🛠️ Development Workflow
                            </h2>
                            <div className="space-y-4">
                                <div className="font-mono text-sm text-gray-300 bg-neutral-800 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <div><span className="text-green-400">1. Local Development:</span> npm run dev</div>
                                        <div><span className="text-blue-400">2. Testing:</span> Cypress E2E tests</div>
                                        <div><span className="text-purple-400">3. Build:</span> npm run build</div>
                                        <div><span className="text-orange-400">4. Deploy:</span> Git push → GitHub Actions → AWS Amplify</div>
                                        <div><span className="text-cyan-400">5. Monitor:</span> CloudWatch logs + Supabase dashboard</div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-2">Quality Assurance</h3>
                                    <ul className="text-gray-300 text-sm space-y-1">
                                        <li>• ESLint + Prettier for code formatting</li>
                                        <li>• TypeScript for type safety where applicable</li>
                                        <li>• Cypress for end-to-end testing</li>
                                        <li>• Manual testing protocols for new features</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/blocks" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-purple-400 mb-1">Block System →</div>
                                    <div className="text-gray-300 text-sm">Learn about the expandable content block architecture</div>
                                </Link>
                                <Link href="/support/documentation/database" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-blue-400 mb-1">Database Schema →</div>
                                    <div className="text-gray-300 text-sm">Understand the PostgreSQL data relationships</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}